/**
 * GDELT global event data source.
 * Fetches recent articles from the GDELT Global Database of Events, Language, and Tone.
 * Free, no API key required. CC BY 4.0.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { getCountryCentroid } from '@/config/country-centroids';
import { djb2Hash } from '@/utils/hash';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'gdelt';

function toneToSeverity(tone: number): Severity {
  if (tone < -5) return 'high';
  if (tone < -2) return 'medium';
  return 'info';
}

interface GdeltArticle {
  url: string;
  title: string;
  seendate: string; // "20240101T120000Z"
  domain: string;
  language: string;
  socialimage?: string;
  sourcecountry?: string;
  tone?: number | string;
}

interface GdeltResponse {
  articles?: GdeltArticle[];
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const gdeltSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'GDELT',
    description: 'Global Database of Events, Language, and Tone',
    icon: 'globe',
    category: 'news',
    updateInterval: 900_000, // 15 min
    requiresApiKey: false,
    dataLicense: 'CC BY 4.0',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] GDELT event source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const url = `${getApiBase()}/api/data/gdelt`;
      const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });

      if (!response.ok) {
        throw new Error(`GDELT API returned ${response.status}`);
      }

      // GDELT may return text error messages instead of JSON
      const text = await response.text();
      let data: GdeltResponse;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(`GDELT returned non-JSON response: ${text.slice(0, 100)}`);
      }

      if (!data.articles || !Array.isArray(data.articles)) {
        return [];
      }

      const events: NormalizedEvent[] = [];

      for (const article of data.articles) {
        if (!article.url || !article.title) continue;

        // GDELT tone can be a comma-separated string (AvgTone,PosScore,NegScore,...)
        const rawTone = article.tone;
        const tone = typeof rawTone === 'string'
          ? parseFloat(rawTone.split(',')[0]) || 0
          : (rawTone ?? 0);

        // Parse GDELT date format: "20240101T120000Z"
        let timestamp: string;
        try {
          const d = article.seendate;
          if (d && d.length >= 15) {
            const iso = `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}T${d.slice(9, 11)}:${d.slice(11, 13)}:${d.slice(13, 15)}Z`;
            timestamp = new Date(iso).toISOString();
          } else {
            timestamp = new Date().toISOString();
          }
        } catch {
          timestamp = new Date().toISOString();
        }

        const event: NormalizedEvent = {
          id: `gdelt-${djb2Hash(article.url)}`,
          source: SOURCE_ID,
          timestamp,
          type: 'news',
          title: article.title,
          severity: toneToSeverity(tone),
          tags: ['gdelt', article.domain, article.language].filter(Boolean),
          links: [{ url: article.url, label: article.domain || 'Source' }],
          raw: {
            tone,
            domain: article.domain,
            language: article.language,
            socialimage: article.socialimage,
            sourcecountry: article.sourcecountry,
          },
        };

        // Try to geo-locate by source country using centroid lookup
        if (article.sourcecountry) {
          const centroid = getCountryCentroid(article.sourcecountry);
          if (centroid) {
            event.location = {
              lat: centroid[0],
              lng: centroid[1],
              country: article.sourcecountry,
              name: article.sourcecountry,
            };
          }
        }

        events.push(event);
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
